import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { trackLanguageChange } from '@/services/analytics';
import { supportedLanguages, type SupportedLanguage } from '../../i18n';
import { Flag } from './ui/Flag';

/**
 * Language selector dropdown for the Planner
 * Displays a flag + language name, with dropdown to switch languages
 */
export const LanguageSelector: React.FC = () => {
    const { i18n } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const currentLang = supportedLanguages.find(l => l.code === i18n.language)
        || supportedLanguages[0]; // Default to French

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleLanguageChange = (code: SupportedLanguage) => {
        i18n.changeLanguage(code);
        trackLanguageChange(code);
        setIsOpen(false);
    };

    // Sync HTML lang attribute with current language
    useEffect(() => {
        document.documentElement.lang = i18n.language;
    }, [i18n.language]);

    return (
        <div className="relative" ref={dropdownRef} translate="no">
            {/* Current language button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center gap-2 px-3 py-1.5 bg-zinc-800/50 hover:bg-zinc-700 text-zinc-300 hover:text-white rounded-lg border border-zinc-700/50 hover:border-zinc-600 transition-all text-sm"
                title="Changer de langue / Change language"
            >
                <Flag langCode={currentLang.code} size={20} />
                <span className="hidden sm:inline">{currentLang.code.toUpperCase()}</span>
                <span className="material-symbols-outlined text-sm">
                    {isOpen ? 'expand_less' : 'expand_more'}
                </span>
            </button>

            {/* Dropdown menu */}
            {isOpen && (
                <div className="absolute right-0 top-full mt-1 w-44 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-50 overflow-hidden">
                    {supportedLanguages.map((lang) => (
                        <button
                            key={lang.code}
                            onClick={() => handleLanguageChange(lang.code)}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-zinc-700 transition-colors ${lang.code === currentLang.code
                                ? 'bg-zinc-700/50 text-primary'
                                : 'text-zinc-300'
                                }`}
                        >
                            <Flag langCode={lang.code} size={24} />
                            <span className="flex-1">{lang.name}</span>
                            {lang.code === currentLang.code && (
                                <span className="material-symbols-outlined text-primary text-sm">check</span>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

